import axios from 'axios';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { restHost } from '../../apiConfig';
import { deleteToken, formatUrl, getCookie } from '../../shared/utils';
import {
  DeviceActionType,
  EnvSwitchTarget,
  REACT_ERROR_CODES,
} from '../../shared/constants';
import {
  Device,
  DeviceActionTypes,
  DevicesStore,
  DeviceStatus,
  ShadowConfig,
  BBImageSetMetadata,
  DeviceSyncPayload,
  SetHistoryLoadingAction,
  DeviceHistoryItem,
  FetchDeviceHistoryAction,
  DeviceFault,
  FetchDeviceFaultsAction,
} from './deviceTypes';

export const populateDevices = (deviceList: Device[]) => ({
  type: DeviceActionTypes.POPULATE_DEVICES,
  payload: { deviceList },
});

export const clearDeviceList = () => ({
  type: DeviceActionTypes.CLEAR_DEVICE_LIST,
});

export const updateDeviceStatus = (
  deviceSerialNumber: string,
  deviceStatus: DeviceStatus,
) => ({
  type: DeviceActionTypes.UPDATE_DEVICE_STATUS,
  payload: { deviceSerialNumber, deviceStatus },
});

export const updateSelectedDeviceStatus = (status: string) => ({
  type: DeviceActionTypes.UPDATE_SELECTED_DEVICE_STATUS,
  payload: { status },
});

export const setDeviceActionSuccess = (type: DeviceActionType) => ({
  type: DeviceActionTypes.DEVICE_ACTION_SUCCESS,
  payload: { type },
});

export const setDeviceActionFailed = (type: DeviceActionType) => ({
  type: DeviceActionTypes.DEVICE_ACTION_FAILED,
  payload: { type },
});

export const clearDeviceActionStatus = () => ({
  type: DeviceActionTypes.CLEAR_DEVICE_ACTION_STATUS,
});

export const getDeviceList = (
  facility_assigned?: boolean,
  owner_assigned?: boolean,
) => {
  return (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, `devices`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
        params: {
          facility_assigned,
          owner_assigned,
        },
      })
      .then((res) => {
        dispatch(setLoadState(false));
        dispatch(populateDevices(res.data));
      })
      .catch((err) => {
        if (err.response && err?.response?.status === 401) {
          deleteToken();
        }
        console.error(err);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        toast.error('Failed to load devices');
      });
  };
};

export const syncDevice = (data: DeviceSyncPayload) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(restHost, `devices/validate_sync_code?assign=true`),
        data,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.request?.status === 400) {
        dispatch(apiError('Invalid sync code'));
        dispatch(
          failureSyncDevice({
            error_code: REACT_ERROR_CODES.INVALID_SYNC_CODE,
            error_message: 'Invalid sync code',
          }),
        );
        dispatch(setLoadState(false));
      }

      if (
        response.request?.status === 403 ||
        response.request?.status === 500
      ) {
        dispatch(apiError('Device assignment failed'));
        dispatch(
          failureSyncDevice({
            error_code: REACT_ERROR_CODES.INVALID_PARAM,
            error_message: 'Device assignment failed!',
          }),
        );
        dispatch(setLoadState(false));
      }

      if (response.request?.status === 200) {
        toast.success('Device added successfully!');
        dispatch(didSyncDevice());
        dispatch(setLoadState(false));
      }
    } catch (error: any) {
      if (error.request) {
        console.log('request error');
      } else if (error.response) {
        console.log('response error');
      }
    }
  };
};

export const failureSyncDevice = (error: any) => ({
  type: DeviceActionTypes.FAILURE_SYNC_DEVICE,
  payload: { syncDeviceError: error },
});

export const didSyncDevice = () => ({
  type: DeviceActionTypes.DID_SYNC_DEVICE,
});

export const clearSyncDeviceStatus = () => ({
  type: DeviceActionTypes.CLEAR_SYNC_DEVICE_STATE,
});

export const deviceReboot = (device_id: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const res = await axios.post(
        formatUrl(restHost, `devices/${device_id}/sys_admin`),
        { action: 'reboot' },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if ((res as any).response && (res as any).response.status === 500) {
        throw new Error();
      } else if (
        (res as any).response &&
        (res as any).response.status === 503
      ) {
        dispatch(setDeviceActionFailed(DeviceActionType.Reboot));
        toast.error('Device is offline. Try again later.');
      } else {
        dispatch(setDeviceActionSuccess(DeviceActionType.Reboot));
        toast.success('Device reboot command sent successfully!');
      }
    } catch (error: any) {
      dispatch(setDeviceActionFailed(DeviceActionType.Reboot));
      if (error.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to send device reboot command');
      dispatch(apiError(error));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const apiError = (error: any) => ({
  type: DeviceActionTypes.DEVICES_API_ERR,
  payload: { error: error },
});

export const setLoadState = (loadState: boolean) => ({
  type: DeviceActionTypes.DEVICES_SET_LOAD_STATE,
  payload: { isLoading: loadState },
});

export const setNotesLoading = (isLoading: boolean) => ({
  type: DeviceActionTypes.SET_NOTES_LOADING,
  payload: { isLoading },
});

export const setNoteActionStatus = (success: boolean, message: string) => ({
  type: DeviceActionTypes.SET_NOTE_ACTION_STATUS,
  payload: { success, message, show: true },
});

export const clearNoteActionStatus = () => ({
  type: DeviceActionTypes.CLEAR_NOTE_ACTION_STATUS,
});

export const setHistoryLoading = (
  isLoading: boolean,
): SetHistoryLoadingAction => ({
  type: DeviceActionTypes.SET_HISTORY_LOADING,
  payload: { isLoading },
});

export const storeDeviceHistory = (
  history: DeviceHistoryItem[],
): FetchDeviceHistoryAction => ({
  type: DeviceActionTypes.FETCH_DEVICE_HISTORY,
  payload: { history },
});

export const updateDeviceProperties = (
  device_id: number,
  fields: Partial<Device>,
) => {
  return (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .put(formatUrl(restHost, `devices/${device_id}`), fields, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then(() => {
        dispatch(setLoadState(false));
        dispatch(getDeviceList());
        toast.success(`Device updated`);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        toast.error('Failed to update device');
      });
  };
};

export const storeDeviceShadow = (
  deviceId: number,
  shadowConfigs: ShadowConfig[],
) => ({
  type: DeviceActionTypes.STORE_DEVICE_SHADOW,
  payload: { deviceId, shadowConfigs },
});

export const fetchDeviceShadow = (deviceId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, 'devices/' + deviceId + '/shadow'), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        const shadowState = res.data;
        const configFieldNames =
          shadowState &&
          Array.from(
            new Set([
              ...Object.keys(shadowState.reported),
              ...Object.keys(shadowState.desired),
            ]),
          );
        const configs =
          configFieldNames &&
          configFieldNames.map((configName: string) => ({
            configName,
            reportedValue: shadowState.reported[configName],
            desiredValue: shadowState.desired[configName],
          }));
        dispatch(storeDeviceShadow(deviceId, configs));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          deleteToken();
        }
        toast.error('Error getting device shadow');
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const clearDeviceShadow = () => ({
  type: DeviceActionTypes.CLEAR_DEVICE_SHADOW,
});

export const updateDeviceShadow = (
  device_id: number,
  stateUpdate: any,
  options?: { shouldReload?: boolean },
) => {
  return (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .put(formatUrl(restHost, `devices/${device_id}/shadow`), stateUpdate, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then(() => {
        toast.success(`Device Shadow Updated`);
        // Optionally reload the page to keep the classic UI in sync
        if (!options || options.shouldReload !== false) {
          window.location.reload();
        } else {
          // Only fetch new shadow state if we're not reloading
          dispatch(fetchDeviceShadow(device_id));
          dispatch(setLoadState(false));
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        toast.error('Failed to update device shadow');
      });
  };
};

export const storeBBImages = (bbImages: BBImageSetMetadata[]) => ({
  type: DeviceActionTypes.STORE_BB_IMAGES,
  payload: { bbImages },
});

export const getBoundingBoxImages = (startDate?: DateTime) => {
  return (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, 'eng/bounding_box_images'), {
        params: {
          start_date: startDate ? startDate.toISODate() : undefined,
        },
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(setLoadState(false));
        dispatch(storeBBImages(res.data));
      })
      .catch((err) => {
        if (err.response && err?.response?.status === 401) {
          deleteToken();
        }
        console.error(err);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        toast.error('Failed to load bounding box images');
      });
  };
};

export const unassignDevice = (device_id: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const res = await axios.post(
        formatUrl(restHost, `devices/${device_id}/sys_admin`),
        { action: 'factory_reset' },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if ((res as any).response && (res as any).response.status === 500) {
        throw new Error();
      } else if (
        (res as any).response &&
        (res as any).response.status === 503
      ) {
        dispatch(setDeviceActionFailed(DeviceActionType.FactoryReset));
        toast.error('Device is offline. Try again later.');
      } else {
        dispatch(setDeviceActionSuccess(DeviceActionType.FactoryReset));
        toast.success('Device factory reset command sent successfully!');
      }
    } catch (error: any) {
      dispatch(setDeviceActionFailed(DeviceActionType.FactoryReset));
      if (error.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to send device factory reset command');
      dispatch(apiError(error));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const deleteDatabase = (device_id: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const res = await axios.post(
        formatUrl(restHost, `devices/${device_id}/sys_admin`),
        { action: 'delete_hub_db' },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if ((res as any).response && (res as any).response.status === 500) {
        throw new Error();
      } else if (
        (res as any).response &&
        (res as any).response.status === 503
      ) {
        dispatch(setDeviceActionFailed(DeviceActionType.DeleteHubDb));
        toast.error('Device is offline. Try again later.');
      } else {
        dispatch(setDeviceActionSuccess(DeviceActionType.DeleteHubDb));
        toast.success('Device database deletion command sent successfully!');
      }
    } catch (error: any) {
      dispatch(setDeviceActionFailed(DeviceActionType.DeleteHubDb));
      if (error.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to send device database deletion command');
      dispatch(apiError(error));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const fetchDeviceById = (deviceId: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `devices/${deviceId}`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch({
        type: DeviceActionTypes.FETCH_DEVICE_BY_ID,
        payload: { device: response.data },
      });
      dispatch(setLoadState(false));
    } catch (err: any) {
      if (err.response && err?.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      dispatch(setLoadState(false));
      toast.error('Failed to fetch device by ID');
    }
  };
};

export const fetchDeviceNotes = (deviceId: number, issueId?: string) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setNotesLoading(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `devices/${deviceId}/note`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
          params: {
            issue_id: issueId,
          },
        },
      );
      if (response.status === 200) {
        dispatch({
          type: DeviceActionTypes.FETCH_DEVICE_NOTES,
          payload: { deviceNotes: response.data },
        });
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      toast.error('Failed to fetch device notes');
    } finally {
      dispatch(setNotesLoading(false));
    }
  };
};

export const addDeviceNote = (
  deviceId: number,
  note: string,
  category: string,
  issueId?: string,
) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setNotesLoading(true));
    try {
      const requestBody = {
        note,
        category,
        ...(issueId && { issue_id: issueId }), // Only include issue_id if it exists
      };

      const response = await axios.post(
        formatUrl(restHost, `devices/${deviceId}/note`),
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.status === 200) {
        // Pass issue_id to fetchDeviceNotes if it exists
        dispatch(fetchDeviceNotes(deviceId, issueId));
        dispatch(setNotesLoading(false));
        toast.success('Note added successfully', { autoClose: 2000 });
      } else {
        dispatch(setNotesLoading(false));
        toast.error('Failed to add device note');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      dispatch(setNotesLoading(false));
      toast.error('Failed to add device note');
    }
  };
};

export const updateDeviceNote = (
  deviceId: number,
  noteId: number,
  note: string,
  category: string,
) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setNotesLoading(true));
    try {
      const response = await axios.put(
        formatUrl(restHost, `devices/${deviceId}/note`),
        { note_id: noteId, note, category },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      if (response.status === 200) {
        dispatch({
          type: DeviceActionTypes.UPDATE_DEVICE_NOTE,
          payload: { deviceNote: response.data },
        });
        dispatch(setNotesLoading(false));
        dispatch(setNoteActionStatus(true, 'Note saved successfully'));
      } else {
        dispatch(setNoteActionStatus(false, 'Note didn’t save'));
        dispatch(setNotesLoading(false));
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      dispatch(setNotesLoading(false));
      dispatch(setNoteActionStatus(false, 'Note didn’t save'));
    }
  };
};

export const deleteDeviceNote = (
  deviceId: number,
  noteId: number,
  deleteAll: boolean = false,
) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setNotesLoading(true));
    try {
      const response = await axios.delete(
        formatUrl(restHost, `devices/${deviceId}/note`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
          params: { delete_all: deleteAll },
          data: { note_id: noteId },
        },
      );
      if (response.status === 200) {
        dispatch({
          type: DeviceActionTypes.DELETE_DEVICE_NOTE,
          payload: { noteId },
        });
        dispatch(setNotesLoading(false));
        dispatch(setNoteActionStatus(true, 'Note deleted successfully'));
      } else {
        dispatch(setNotesLoading(false));
        dispatch(setNoteActionStatus(false, 'Deletion unsuccessful'));
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      dispatch(setNotesLoading(false));
      dispatch(setNoteActionStatus(false, 'Deletion unsuccessful'));
    }
  };
};

export const updateDeviceSoftware = (
  device_id: number,
  pinned_software_version_id?: number | null,
  force?: boolean,
) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const payload: {
        action: string;
        pinned_software_version_id?: number | null;
        force?: boolean;
      } = {
        action: 'update_sw_version',
      };
      // Only include pinned_software_version_id if it's explicitly provided
      if (pinned_software_version_id !== undefined) {
        payload.pinned_software_version_id = pinned_software_version_id;
      }
      // force=true tells the Hub to auto-start with no skip prompt.
      if (force) {
        payload.force = true;
      }
      const res = await axios.post(
        formatUrl(restHost, `devices/${device_id}/sys_admin`),
        payload,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if ((res as any).response?.status === 500) {
        throw new Error();
      } else if ((res as any).response?.status === 503) {
        toast.error('Device is offline. Try again later.');
        dispatch(setDeviceActionFailed(DeviceActionType.SoftwareUpdate));
      } else {
        dispatch(setDeviceActionSuccess(DeviceActionType.SoftwareUpdate));
        toast.success('Software update command sent successfully!');
        dispatch(fetchDeviceById(device_id));
      }
    } catch (error: any) {
      dispatch(setDeviceActionFailed(DeviceActionType.SoftwareUpdate));
      if (error.response?.status === 401) {
        deleteToken();
      }
      toast.error('Failed to send software update command');
      dispatch(apiError(error));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const unassignDeviceToOwner = (device_id: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const res = await axios.post(
        formatUrl(restHost, `devices/${device_id}/unassign`),
        {},
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if ((res as any).response?.status === 500) {
        throw new Error();
      } else if ((res as any).response?.status === 503) {
        dispatch(setDeviceActionFailed(DeviceActionType.UnassignOwner));
        toast.error('Device is offline. Try again later.');
      } else {
        dispatch(setDeviceActionSuccess(DeviceActionType.UnassignOwner));
        // Refresh device data
        dispatch(fetchDeviceById(device_id));
      }
    } catch (error: any) {
      dispatch(setDeviceActionFailed(DeviceActionType.UnassignOwner));
      if (error.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(error));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const fetchDeviceHistory = (deviceId: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setHistoryLoading(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `devices/${deviceId}/history`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(storeDeviceHistory(response.data));
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      toast.error('Failed to fetch device history');
    } finally {
      dispatch(setHistoryLoading(false));
    }
  };
};

export const linkDeviceToOwner = (
  deviceId: number,
  ownerId: number,
  force: boolean = false,
) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const response = await axios.post(
        formatUrl(restHost, `devices/${deviceId}/register/${ownerId}`),
        { force },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      if (response.status === 200) {
        dispatch(setDeviceActionSuccess(DeviceActionType.LinkDeviceToOwner));
      } else {
        toast.error(
          (response as any).response?.data || 'Failed to link device to owner',
          {
            autoClose: 2000,
          },
        );
      }
    } catch (error: any) {
      dispatch(setDeviceActionFailed(DeviceActionType.LinkDeviceToOwner));
      if (error.response?.status === 401) {
        deleteToken();
      }
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const setFaultsLoading = (isLoading: boolean) => ({
  type: DeviceActionTypes.SET_FAULTS_LOADING,
  payload: { isLoading },
});

export const setFaultsDeleting = (isDeleting: boolean) => ({
  type: DeviceActionTypes.SET_FAULTS_DELETING,
  payload: { isDeleting },
});

export const storeDeviceFaults = (
  deviceFaults: DeviceFault[],
): FetchDeviceFaultsAction => ({
  type: DeviceActionTypes.FETCH_DEVICE_FAULTS,
  payload: { deviceFaults },
});

export const fetchDeviceFaults = (deviceId: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setFaultsLoading(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `devices/${deviceId}/faults`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(
        storeDeviceFaults(Array.isArray(response.data) ? response.data : []),
      );
    } catch (err: any) {
      if (err.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      toast.error('Failed to fetch device faults');
      dispatch(storeDeviceFaults([]));
    } finally {
      dispatch(setFaultsLoading(false));
    }
  };
};

export const deleteDeviceFaults = (deviceId: number) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setFaultsDeleting(true));
    try {
      await axios.delete(formatUrl(restHost, `devices/${deviceId}/faults`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      });
      toast.success('Unresolved faults deleted successfully');
      dispatch(fetchDeviceFaults(deviceId));
    } catch (err: any) {
      if (err.response?.status === 401) {
        deleteToken();
      }
      dispatch(apiError(err));
      toast.error('Failed to delete unresolved faults');
    } finally {
      dispatch(setFaultsDeleting(false));
    }
  };
};

export interface EnvSwitchPayload {
  target_env: EnvSwitchTarget;
  target_rest_host: string;
  image_url: string;
  sig_url: string;
  key_url: string;
  version: string;
}

export const sendEnvSwitch = (device_id: number, payload: EnvSwitchPayload) => {
  return async (
    dispatch: ThunkDispatch<DevicesStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    try {
      const res = await axios.post(
        formatUrl(restHost, `devices/${device_id}/sys_admin`),
        { action: 'env_switch', ...payload },
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );

      const status = res.status || (res as any).response?.status;
      if (status >= 200 && status < 300) {
        dispatch(setDeviceActionSuccess(DeviceActionType.EnvSwitch));
        toast.success('Environment switch command sent successfully!');
      } else if (status === 401) {
        dispatch(setDeviceActionFailed(DeviceActionType.EnvSwitch));
        deleteToken();
      } else if (status === 403) {
        dispatch(setDeviceActionFailed(DeviceActionType.EnvSwitch));
        toast.error(
          'You do not have permission to issue environment switch commands.',
        );
      } else if (status === 503) {
        dispatch(setDeviceActionFailed(DeviceActionType.EnvSwitch));
        toast.error('Device is offline or MQTT unreachable. Try again later.');
      } else {
        dispatch(setDeviceActionFailed(DeviceActionType.EnvSwitch));
        const detail = (res as any).response?.data;
        const msg =
          typeof detail === 'string' && !detail.trimStart().startsWith('<')
            ? detail
            : (detail?.message ?? `Server error (${status})`);
        toast.error(`Environment switch failed: ${msg}`);
      }
    } catch (error: any) {
      dispatch(setDeviceActionFailed(DeviceActionType.EnvSwitch));
      toast.error('Environment switch failed: network error.');
      dispatch(apiError(error));
    } finally {
      dispatch(setLoadState(false));
    }
  };
};
